import { z } from "zod";
import { ModalContextInterface } from "./context/ModalContext";
import { SurveyInfo, getSOID } from "./qtrax_utils/form_util";
import { StoreRespChema } from "./schemas";
import { IContactRespData, ModalMessage, StorePersonnelPayloadInterface } from "./types";
import { API_INFO, logAutofillMessage } from "./util/api";

export const SKIP_DISTRO_CHECK_PREFIX = "Skipping Distro Check";

export class ModalHandler {
  private readonly DEFAULT_BARCODE_SHEET_TYPE = "out-of-dist";

  private _context: ModalContextInterface;
  private openModelButtonId: string;

  constructor(context: ModalContextInterface, openModelButtonId: string) {
    this.openModelButtonId = openModelButtonId;
    this._context = context;
  }

  get context() {
    return this._context;
  }

  private get openModalButton() {
    return document.getElementById(this.openModelButtonId) as HTMLButtonElement;
  }

  showModal() {
    if (!this.openModalButton.parentElement!.classList.contains("show")) {
      this.openModalButton.click();
    }
  }

  resetContext() {
    this._context.resetContext();
  }

  clearFillStatus() {
    this._context.setFillMessages(() => []);
  }

  clearAllStatus() {
    this._context.setFillMessages(() => []);
    this._context.setErrorMessages(() => []);
  }

  addErrorMsg(message: string, messageType: ModalMessage["messageType"] = "general") {
    const newModalMessage = { text: message, isError: true, messageType } satisfies ModalMessage;
    this._context.setErrorMessages((prevMessages) => {
      return [...prevMessages, newModalMessage];
    });
  }

  addErrorMessages(messages: string[], messageType: ModalMessage["messageType"] = "general") {
    const newModalMessages = messages.map((msg) => ({
      text: msg,
      isError: true,
      messageType,
    })) satisfies ModalMessage[];

    this._context.setErrorMessages((prevMessages) => {
      return [...prevMessages, ...newModalMessages];
    });
  }

  addWarningMsg(message: string, messageType: ModalMessage["messageType"] = "general") {
    const newModalMessage = { text: message, isWarning: true, messageType } satisfies ModalMessage;
    this._context.setErrorMessages((prevMessages) => {
      return [...prevMessages, newModalMessage];
    });
  }

  addFillStatusMsg(...msgs: string[]) {
    const newMessages = msgs.map((msg) => {
      return { text: msg, messageType: "fill-status" };
    }) satisfies ModalMessage[];

    this._context.setFillMessages((prevMessages) => [...prevMessages, ...newMessages]);
  }

  getFinalOutputMessages(surveyInfo: SurveyInfo): ModalMessage[] {
    const isWarnNewProducts =
      surveyInfo.total_new_products > 0 && surveyInfo.total_new_and_scanned_products === 0;

    const outputMessages = [
      {
        text: `${surveyInfo.total_items} total items (${surveyInfo.total_scanned_products} scanned)`,
        messageType: "fill-status",
      },
      {
        text: `${surveyInfo.total_new_products} new items (${surveyInfo.total_new_and_scanned_products} scanned)`,
        isWarning: isWarnNewProducts,
        messageType: "fill-status",
      },
      {
        text: `Confirmed in stock: ${surveyInfo.verboseFillResults["Confirmed in stock"]}`,
        messageType: "fill-status",
      },
      { text: `Added stock: ${surveyInfo.verboseFillResults["Added stock"]}`, messageType: "fill-status" },
      {
        text: `In-Dist Ordered: ${surveyInfo.verboseFillResults["In-Dist Ordered"]}`,
        messageType: "fill-status",
      },
      {
        text: `Out-Of-Dist Ordered: ${surveyInfo.verboseFillResults["Out-Of-Dist Ordered"]}`,
        messageType: "fill-status",
      },
    ] satisfies ModalMessage[];

    return outputMessages;
  }

  showFinalStatus(outputMessages: ModalMessage[]) {
    this.clearFillStatus();
    this._context.setFillMessages(() => outputMessages);
  }

  setStoreData(storeData: z.infer<typeof StoreRespChema>) {
    this._context.setStoreInfo(() => storeData);
  }

  setClientName(name: string) {
    this._context.setClientName(() => name);
  }

  setIsFilling(targetIsFillingValue: boolean) {
    this._context.setIsFillSuccessful(() => !targetIsFillingValue);
    if (!targetIsFillingValue) {
      this.logMessages();
    }
  }

  updateBarcodeLinkButton(barcodeSheetId: number | undefined) {
    if (barcodeSheetId === undefined) {
      return;
    }

    this._context.setIsFillSuccessful(() => true);

    const barcodeLinkNode = document.getElementById("fs-util-link-get-barcodes") as HTMLAnchorElement;
    const barcodeUrlObj = new URL(`${API_INFO.api_host}/logger/barcode_sheet/${barcodeSheetId}/`);
    // barcode_url_obj.searchParams.append("store-name", store_name);
    barcodeUrlObj.searchParams.append("sheet-type", this.DEFAULT_BARCODE_SHEET_TYPE);

    barcodeLinkNode.href = barcodeUrlObj.href;
    barcodeLinkNode.target = "_blank";
  }

  populatePersonnelInfoForm(personnelContacts: IContactRespData[], storeId: number) {
    const contactInfo: IContactRespData = personnelContacts[0] ?? {
      id: null,
      first_name: "",
      last_name: "",
    };

    this._context.setPersonnelUpdateInfo((): StorePersonnelPayloadInterface => {
      return {
        existing_personnel_id: contactInfo.id,
        new_personnel_first_name: contactInfo.first_name,
        new_personnel_last_name: contactInfo.last_name,
        store_id: storeId,
      };
    });
  }

  private logMessages() {
    const errorMessages = this.context
      .errorMessages()
      .filter((msg) => {
        if (
          msg.messageType !== "fill-status" &&
          msg.isError === true &&
          !msg.text.includes(SKIP_DISTRO_CHECK_PREFIX)
        )
          return msg.text;
      })
      .map((msg) => msg.text);

    const warningMessages = this.context
      .errorMessages()
      .filter((msg) => {
        if (
          msg.messageType !== "fill-status" &&
          msg.isWarning === true &&
          !msg.text.includes(SKIP_DISTRO_CHECK_PREFIX)
        )
          return msg.text;
      })
      .map((msg) => msg.text);

    errorMessages.length > 0 && void logAutofillMessage(getSOID(), "error", errorMessages.join("\n"));
    warningMessages.length > 0 && void logAutofillMessage(getSOID(), "warning", warningMessages.join("\n"));
  }
}
